import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import { Modal, Card, Grid, Divider, CircularProgress } from "@mui/material";

// Material Dashboard 2 React components (assumed)
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// API import
import api from "../../../../utils/axiosConfig";

function ProfileUpdateModal({ open, onClose, profileData }) {
  // États du formulaire, initialisés avec les données du profil
  const [name, setName] = useState(profileData.name);
  const [email, setEmail] = useState(profileData.email);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    // 1. Validation des mots de passe
    if (newPassword && newPassword !== passwordConfirmation) {
      setFormError("Les mots de passe ne correspondent pas.");
      setIsSubmitting(false);
      return;
    }

    // 2. Construction du payload
    const payload = { name, email };

    if (newPassword) {
      if (!currentPassword) {
        setFormError("Le mot de passe actuel est requis pour le modifier.");
        setIsSubmitting(false);
        return;
      }
      payload.current_password = currentPassword;
      payload.password = newPassword;
      payload.password_confirmation = passwordConfirmation;
    }

    // 3. Appel à l'API
    try {
      const response = await api.put("/api/auth/update-profile", payload);

      onClose();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Échec de la mise à jour du profil.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <MDBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95vw", sm: "90vw", md: 500 },
        }}
      >
        <Card sx={{ p: 4 }}>
          <MDTypography variant="h5" mb={2}>
            Mettre à jour le Profil
          </MDTypography>
          <Divider />

          {formError && (
            <MDTypography color="error" variant="caption" mb={2}>
              {formError}
            </MDTypography>
          )}

          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  label="Nom Complet"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Email"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text">
                  Laissez les champs de mot de passe vides si vous ne souhaitez pas les modifier.
                </MDTypography>
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Mot de Passe Actuel"
                  fullWidth
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Nouveau Mot de Passe"
                  fullWidth
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Confirmer le Mot de Passe"
                  fullWidth
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
            <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
              <MDButton
                variant="contained"
                color="secondary"
                onClick={onClose}
                sx={{ mr: 1 }}
                disabled={isSubmitting}
              >
                Annuler
              </MDButton>
              <MDButton type="submit" variant="gradient" color="purple" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={20} color="white" /> : "Mettre à jour"}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </Modal>
  );
}

// Don't forget PropTypes!
ProfileUpdateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  profileData: PropTypes.object.isRequired,
};

export default ProfileUpdateModal;
